import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CallButton from "@/components/CallButton";
import SEO from "@/components/SEO";
import ServiceStructuredData from "@/components/ServiceStructuredData";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOSubHero from "@/components/SEOSubHero";
import { Link } from "react-router-dom";
import { Phone, Shield, Key, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import heroImage from "@/assets/heroes/changement-serrure-hero.jpg";
import gallery1 from "@/assets/gallery/changement-serrure-1.jpg";
import gallery2 from "@/assets/gallery/changement-serrure-2.jpg";
import gallery3 from "@/assets/gallery/changement-serrure-3.jpg";

const ChangementSerrure = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Changement de Serrure Villeurbanne | Installation Rapide 24h/7j"
        description="Changement de serrure à Villeurbanne : cylindre, serrure multipoints, serrure encastrée. Installation professionnelle rapide. Devis gratuit, intervention 24h/7j."
        keywords="changement serrure Villeurbanne, remplacement cylindre Lyon, installation serrure multipoints, serrurier changement serrure 69"
        canonical="https://www.tolstoi-serrurier.fr/changement-serrure"
        ogUrl="https://www.tolstoi-serrurier.fr/changement-serrure"
        ogImage="https://www.tolstoi-serrurier.fr/logo.jpg"
        ogType="business.business"
      />
      <ServiceStructuredData 
        serviceName="Changement de serrure"
        serviceDescription="Service de changement et installation de serrures à Villeurbanne : cylindre, multipoints, serrure encastrée. Intervention rapide 24h/7j."
        serviceUrl="https://www.tolstoi-serrurier.fr/changement-serrure"
      />
      <Header />
      
      <div className="container mx-auto px-4">
        <Breadcrumbs items={[
          { label: "Accueil", href: "/" },
          { label: "Changement de serrure", href: "/changement-serrure" }
        ]} />
      </div>
      
      <main className="pt-24">
        {/* Hero Section with Background Image */}
        <section className="relative bg-secondary text-secondary-foreground py-16 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={heroImage} 
              alt="Serrurier Villeurbanne - Changement de serrure et cylindre"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-secondary/90 to-secondary/70" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Changement de Serrure à Villeurbanne
              </h1>
              <p className="text-xl mb-8 text-secondary-foreground/90">
                Installation et remplacement de tous types de serrures par un professionnel certifié
              </p>
              <Button size="lg" variant="default" className="gap-2" asChild>
                <a href="tel:0766009189">
                  <Phone className="h-5 w-5" />
                  Appeler maintenant : 07 66 00 91 89
                </a>
              </Button>
            </div>
          </div>
        </section>

        <SEOSubHero title="Changement de serrure à Villeurbanne — conseils et installation A2P">
          <p>
            Remplacement de <strong>cylindre européen</strong>, pose de <strong>serrures multipoints A2P</strong> et <strong>serrures encastrées</strong>. Nous intervenons à Villeurbanne et Lyon après déménagement, <strong>perte de clés</strong> ou pour renforcer la sécurité de votre logement.
          </p>
          <p>
            Nous vous conseillons sur le <strong>niveau de protection</strong> adapté et les marques reconnues. Devis <strong>gratuit et transparent</strong> avant travaux. Complétez votre sécurisation avec une <Link to="/porte-blindee-garage" className="text-primary hover:underline">porte blindée</Link> ou consultez nos pages{" "}
            <Link to="/reparation-serrure" className="text-primary hover:underline">réparation de serrure</Link> et{" "}
            <Link to="/ouverture-porte" className="text-primary hover:underline">ouverture de porte</Link>.
          </p>
          <p>
            Intervention rapide sur Charpennes, Gratte-Ciel, Cusset, République, et toute la métropole. <Link to="/zones-intervention" className="text-primary hover:underline">Voir les zones</Link>.
          </p>
        </SEOSubHero>

        {/* Main Content */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg max-w-none mb-12">
                <h2 className="text-3xl font-bold mb-6">Service de changement de serrure professionnel</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Tolstoï Serrurier vous accompagne dans le changement de votre serrure à Villeurbanne et dans 
                  toute la métropole lyonnaise. Que ce soit après un cambriolage, une perte de clés, ou simplement 
                  pour améliorer la sécurité de votre domicile, nous installons des serrures de qualité adaptées 
                  à vos besoins.
                </p>
                
                <h3 className="text-2xl font-semibold mb-4 mt-8">Types de serrures installées</h3>
                <p className="text-muted-foreground mb-6">
                  Notre serrurier est qualifié pour installer tous types de serrures, du simple cylindre européen 
                  aux serrures multipoints haute sécurité. Nous travaillons avec les meilleures marques du marché 
                  pour vous garantir une protection optimale.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <Card>
                  <CardContent className="pt-6">
                    <Key className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Cylindre européen</h3>
                    <p className="text-sm text-muted-foreground">
                      Remplacement de cylindre, installation de cylindre haute sécurité anti-crochetage, anti-perçage.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <Shield className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Serrure multipoints</h3>
                    <p className="text-sm text-muted-foreground">
                      Installation de serrures 3, 5 ou 7 points certifiées A2P pour une sécurité renforcée.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <CheckCircle2 className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Serrure encastrée</h3>
                    <p className="text-sm text-muted-foreground">
                      Pose de serrures encastrées dans la porte, invisibles et esthétiques.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-muted/50 rounded-xl p-8 mb-12">
                <h3 className="text-2xl font-semibold mb-4">Quand changer sa serrure ?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Après un cambriolage ou une tentative d'effraction</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>En cas de perte ou vol de clés</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Lors d'un déménagement (ancien ou nouveau logement)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Pour améliorer la sécurité de votre domicile</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Si la serrure est vétuste ou difficile à manipuler</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Pour remplacer une serrure non conforme aux normes d'assurance</span>
                  </li>
                </ul>
              </div>

              <div className="bg-primary/10 rounded-xl p-8 mb-12">
                <h3 className="text-2xl font-semibold mb-4">Nos marques de serrures</h3>
                <p className="text-muted-foreground mb-4">
                  Nous travaillons exclusivement avec des marques reconnues pour leur qualité et leur fiabilité :
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-background p-4 rounded-lg text-center font-semibold">Fichet</div>
                  <div className="bg-background p-4 rounded-lg text-center font-semibold">Bricard</div>
                  <div className="bg-background p-4 rounded-lg text-center font-semibold">Vachette</div>
                  <div className="bg-background p-4 rounded-lg text-center font-semibold">Pollux</div>
                </div>
              </div>

              <div className="bg-secondary text-secondary-foreground rounded-xl p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Besoin de changer votre serrure ?</h3>
                <p className="text-lg mb-6 text-secondary-foreground/90">
                  Contactez-nous pour un devis gratuit et une installation rapide
                </p>
                <Button size="lg" variant="default" className="gap-2" asChild>
                  <a href="tel:0766009189">
                    <Phone className="h-5 w-5" />
                    07 66 00 91 89
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Photo Gallery Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">Nos Interventions en Images</h2>
              <p className="text-center text-muted-foreground mb-12">Découvrez la qualité de notre travail</p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img src={gallery1} alt="Comparaison ancien cylindre usé et nouveau cylindre haute sécurité Villeurbanne" loading="lazy" className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img src={gallery2} alt="Installation serrure multipoints par serrurier professionnel Lyon" loading="lazy" className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img src={gallery3} alt="Nouveau cylindre de serrure installé finition professionnelle Villeurbanne" loading="lazy" className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <CallButton />
    </div>
  );
};

export default ChangementSerrure;
